// Registry of every key the app persists. Adding an entry here is how storage stops
// being a junk drawer: it gives us an inventory, lets us clear by scope instead of
// nuking every portal's session, and gives one place to reason about what survives a
// reload. Prefer a registry entry over a raw string when calling the storage API.

export const SCOPES = {
  AUTH: 'auth', // portal JWTs
  SESSION: 'session', // who the logged-in user is
  UI: 'ui', // remembered UI preferences, safe to lose
  FLOW: 'flow', // transient multi-step flow state
  DATA: 'data', // cached server data
  SYSTEM: 'system', // app plumbing
}

/**
 * Per-portal JWT keys. `setTokenInLocalStorage(null)` clears exactly this set.
 */
export const PORTAL_TOKENS = {
  scout: { key: 'scout_token', scope: SCOPES.AUTH, portal: 'scout' },
  startup: { key: 'startup_token', scope: SCOPES.AUTH, portal: 'startup' },
  learnInvest: { key: 'learnInvest_token', scope: SCOPES.AUTH, portal: 'learnInvest' },
  series65: { key: 'series65_token', scope: SCOPES.AUTH, portal: 'series65' },
  investor: { key: 'investor_token', scope: SCOPES.AUTH, portal: 'investor' },
  recruit: { key: 'recruit_token', scope: SCOPES.AUTH, portal: 'recruit' },
}

/**
 * Look up the token descriptor for a portal.
 * @param {string} portal - Portal name, e.g. 'scout'.
 * @returns {object|undefined} The key descriptor, or undefined for an unknown portal.
 */
export function tokenKeyFor(portal) {
  return PORTAL_TOKENS[portal]
}

export const KEYS = {
  ...PORTAL_TOKENS,

  // session
  TOKEN: { key: 'token', scope: SCOPES.SESSION },
  USER_ID: { key: 'userId', scope: SCOPES.SESSION },
  USER_DATA: { key: 'userData', scope: SCOPES.SESSION },
  IS_STARTUP: { key: 'is_startup', scope: SCOPES.SESSION },
  STARTUP_ACCOUNT: { key: 'startup_account', scope: SCOPES.SESSION, portal: 'startup' },
  INVESTOR_ACCOUNT: { key: 'investor_account', scope: SCOPES.SESSION, portal: 'investor' },

  // ui
  TOUR_IS_TAKEN: { key: 'tour_is_taken', scope: SCOPES.UI },
  TRACKING_TOUR_IS_TAKEN: { key: 'tracking_tour_is_taken', scope: SCOPES.UI },
  SELECTED_QNA_TAB: { key: 'selectedQnATab', scope: SCOPES.UI },
  BLOG_PAGE: { key: 'blogPage', scope: SCOPES.UI },
  PODCAST_PAGE: { key: 'poadcastpage', scope: SCOPES.UI },

  // flow
  ENTITY_FLOW_SOURCE: { key: 'entity_flow_source', scope: SCOPES.FLOW },
  NEW_BANK_ADDED: { key: 'new_bank_added', scope: SCOPES.FLOW },

  // data
  EXCHANGE_RATE: { key: 'exchange_rate', scope: SCOPES.DATA },
}

/**
 * Keys held in sessionStorage (per-tab), used with the `sessionStore` instance.
 */
export const SESSION_KEYS = {
  POST_LOGIN_REDIRECT: { key: 'postLoginRedirect', scope: SCOPES.FLOW },
  CHUNK_RELOAD_TS: { key: 'chunk_reload_ts', scope: SCOPES.SYSTEM },
  // Which portal this tab was launched from — navigation origin, not identity. Per-tab
  // so it dies with the tab and cannot leak into an unrelated session. See
  // src/utils/portalOrigin.js.
  LOGIN_TYPE: { key: 'login_type', scope: SCOPES.FLOW },
}
